import { useEffect, useState, useCallback } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const UpdateNotification = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);

  const checkForUpdates = useCallback(() => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage('checkForUpdates');
    }
  }, []);

  const handleUpdate = useCallback(() => {
    // Clear all caches
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }

    // Tell service worker to skip waiting
    if (registration && registration.waiting) {
      registration.waiting.postMessage('skipWaiting');
    }

    // Force reload from server
    window.location.reload(true);
  }, [registration]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // Register service worker
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        console.log('[App] Service worker registered');
        setRegistration(reg);

        // Check for updates when a new service worker is found
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('[App] New version available');
                setUpdateAvailable(true);
              }
            });
          }
        });
      })
      .catch((error) => {
        console.log('[App] Service worker registration failed:', error);
      });

    // Listen for messages from service worker
    const handleMessage = (event) => {
      if (event.data?.type === 'UPDATE_CHECK_RESULT' && event.data.hasUpdate) {
        setUpdateAvailable(true);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);

    // Check for updates on mount and periodically
    const checkNow = () => {
      if (navigator.serviceWorker.controller) {
        checkForUpdates();
      }
    };

    // Initial check after a short delay
    const initialCheck = setTimeout(checkNow, 2000);
    
    // Check every 5 minutes while app is open
    const periodicCheck = setInterval(checkNow, 5 * 60 * 1000);

    // Also check when app becomes visible (user returns to app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkNow();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(initialCheck);
      clearInterval(periodicCheck);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [checkForUpdates]);

  if (!updateAvailable) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50 animate-in slide-in-from-bottom-4"
      data-testid="update-notification"
    >
      <div className="bg-[#1A4D2E] text-white rounded-lg shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <RefreshCw className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">Update Available</p>
            <p className="text-xs text-white/80 mt-0.5">
              A new version of MyTrip is ready
            </p>
          </div>
          <button
            onClick={() => setUpdateAvailable(false)}
            className="flex-shrink-0 text-white/60 hover:text-white"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-3 flex gap-2">
          <Button
            onClick={handleUpdate}
            size="sm"
            className="flex-1 bg-white text-[#1A4D2E] hover:bg-white/90"
            data-testid="update-now-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Update Now
          </Button>
          <Button
            onClick={() => setUpdateAvailable(false)}
            size="sm"
            variant="ghost"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            Later
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpdateNotification;
